import { useFormik } from 'formik';
import { usePostHog } from 'posthog-js/react';
import { FC, Fragment, useState } from 'react';
import * as yup from 'yup';

import { avatarDefaultUrl, ImagesApp } from '../../constants/images';
import { TicketType } from '../../constants/tickets';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { shouldFetchFacialRecognitionUrl } from '../../hooks/contexts/helpers/faceRecognition';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import { useUiContext } from '../../hooks/contexts/ui.context';
import { useUserContext } from '../../hooks/contexts/user.context';
import { AssignmentTickets } from '../../types/cart';
import formatNumericValue from '../../utils/formatNumericValue';
import { associatedFaceRecognitionInThisSession } from '../../utils/wallet';
import DateTimeSessionsTags from '../DateTimeSessionsTags';
import { AlertModalContent } from '../FacialRecognition';
import AsideTimelineDate from './AsideTimelineDate';
import ErrorAlert from './ErrorAlert';
import HolderTicketCheckbox from './HolderTicketCheckbox';
import KidsNaming from './KidsNaming';
import * as Styles from './styles.naming';
import TagNaming from './TagNaming';
import UserNaming from './UserNaming';

type NamingTicketProps = {
  namingData: AssignmentTickets;
  isLastItem: boolean;
  id: string;
  isPassport: boolean;
  startWithRfcModal: boolean | null;
  addEmail: (data: { email: string; idTicket: number }) => void;
  checkDuplicateEmail: (data: { email: string; idTicket: number }) => boolean;
  removeEmail: (data: { email: string; idTicket: number }) => void;
};

const initialValues = { email : '' };

const NamingTicket: FC<NamingTicketProps> = (props) => {
  const posthog = usePostHog();
  const { id, isLastItem, namingData, isPassport, addEmail, checkDuplicateEmail, removeEmail } = props;
  const { idSpecificInIdentifyList, transferMandatoryFaceRecognition, holder, faceRecognition, dates } = namingData;

  const [userNotFound, setUserNotFound] = useState(false);
  const [userHasntBepassToken, setUserHasntBepassToken] = useState(false);

  const { user, rfData, getFacialRecognitionUrl } = useUserContext();
  const { translate } = useI18nContext();
  const { currencyFormat, event, ticketsForThisEventInMyWallet, producerId } = useEventContext();
  const { getUserByEmailOrDocument } = useUserContext();
  const { assignTicketToHolder, invalidateAssignTicketToHolder, associateTheTicketForMe } = useCartContext();
  const { toggleModal, setContentModal, defineTitleModal } = useModalContext();
  const { isMobile } = useUiContext();

  const validationSchema = yup.object<{ document: string }>({
    email : yup.string()
      .email(translate(['INFORMATION', 'INVALID_EMAIL']))
      .test('isNotMe', translate(['INFORMATION', 'CANNOT_TRANSFER_TO_MYSELF']), (value) => {
        if (holder) return true;
        return value !== user?.email;
      })
      .test('is-unique', translate(['INFORMATION', 'EMAIL_ALREADY_USED']), (value) => {
        if (event?.attributes.faceRecognition.value) {
          if (value && checkDuplicateEmail({ email : value, idTicket : dates[0].id })) {
            return false;
          }
        }
        return true;
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize : true,
    onSubmit : async ({ email }) => {

      if (event?.attributes.faceRecognition.value) {
        addEmail({ email : email, idTicket : dates[0].id });
      }
      posthog?.capture('puchase:transfer_assign_ticket_select');
      setUserNotFound(false);
      setUserHasntBepassToken(false);
      if (holder) {
        invalidateAssignTicketToHolder({ idSpecificInIdentifyList });
        return;
      }

      invalidateAssignTicketToHolder({ idSpecificInIdentifyList });
      const transferRequiredRegisterFace =
        event?.attributes.transferMandatoryFaceRecognition &&
        event?.attributes.transferMandatoryFaceRecognition.value;

      const validateRfc = (faceRecognition && transferMandatoryFaceRecognition) ?? false;
      const userToNaming = await getUserByEmailOrDocument({ email, checkRfc : validateRfc, eventId : event?.id });
      const hasUser = userToNaming.length > 0;
      const hasTokenFaceRecognition =  Array.isArray(userToNaming) &&
      userToNaming?.some(user => user?.tokenFaceRecognition) || false;

      if (
        validateRfc &&
        !hasTokenFaceRecognition
      ) {
        setUserHasntBepassToken(!hasTokenFaceRecognition);
        return;
      }

      const dontHasUserWarning = !hasUser && transferRequiredRegisterFace;
      if (dontHasUserWarning) return setUserNotFound(true);

      if (hasUser) return assignTicketToHolder({
        holder : { ...userToNaming[0], email },
        idSpecificInIdentifyList,
      });
      const defaultIncompleteHolder = {
        id : Math.random(),
        name : email,
        lastName : email,
        password : email,
        nationality : 'BRA',
        email,
        picture : avatarDefaultUrl,
      };

      // setUserNotFound(true);
      assignTicketToHolder({ holder : defaultIncompleteHolder, idSpecificInIdentifyList });
    },
  });

  const hideErrorMessage = () => {
    setUserNotFound(false);
    formik.resetForm();
  };

  const transferRequiredRegisterFace =
    event?.attributes.transferMandatoryFaceRecognition &&
    event?.attributes.transferMandatoryFaceRecognition.value;
  const isBepassTicket = faceRecognition;

  const isFutebol = event?.categories.some((category) => category.name === 'Futebol');
  const dontHasUserWarning = userNotFound && (isBepassTicket || transferRequiredRegisterFace);
  const containsError = (formik.errors.email && formik.touched.email) || dontHasUserWarning || userHasntBepassToken;
  const theresAHolderAndItsMe = (holder && user && holder.id === user.id) || false;
  const isAKidsTicket = Number(props.namingData.category.id) === TicketType.DEPENDENT;

  const createRfcModal = () => {
    if (user) {
      defineTitleModal(translate(['INFORMATION', 'FACIAL_BIOMETRY_AVAILABLE']));
      setContentModal(
        <AlertModalContent
          associateTheTicketForMe={() => associateTheTicketForMe({ idSpecificInIdentifyList, user })}
          isMandatory={transferMandatoryFaceRecognition}
        />
      );
      toggleModal({ open : true });
    }
  };

  const createBepassAlreadyAssociatedModal = () => {
    defineTitleModal(translate(['INFORMATION', 'FACE_RECOGNITION_ASSOCIATION_ERROR_TITLE']));
    setContentModal(
      <div>
        {translate(['INFORMATION', 'ASSOCIATION_ERROR_FACE_RECOGNITION'])}
      </div>
    );
    toggleModal({ open : true });
  };

  const handleChecked = async () => {
    const shouldFetchRfc = shouldFetchFacialRecognitionUrl({
      event,
      groupHasFaceRecognition : namingData.faceRecognition,
    });
    if (event?.id && shouldFetchRfc) {
      await getFacialRecognitionUrl({ eventId : event.id, producerId });
    }
    if (!user) return;
    setUserHasntBepassToken(false);
    if (namingData.faceRecognition) {
      const hasAssociatedFaceInThisSession = associatedFaceRecognitionInThisSession(
        ticketsForThisEventInMyWallet,
        namingData,
      );
      if(!hasAssociatedFaceInThisSession && !user.faceRecognition.enabled){
        user.faceRecognition.enabled = namingData.faceRecognition;
      }
      if (hasAssociatedFaceInThisSession) return createBepassAlreadyAssociatedModal();
      if (!rfData?.token) return createRfcModal();
    }

    associateTheTicketForMe({ idSpecificInIdentifyList, user });
    formik.setFieldValue('email', '');
  };

  const handleClick = () =>
    (holder && handleRemoveEmail()) ||
    (isAKidsTicket && invalidateAssignTicketToHolder({ idSpecificInIdentifyList }));

  const handleRemoveEmail = () => {
    removeEmail({ email : formik.values.email, idTicket : dates[0].id });
    formik.resetForm();
  };

  const invalidHolder = formik.values.email === '' && !holder;
  const nonTransferable = !namingData.isTransferableGuestType;
  const fanMember = Number(namingData.category.id) === TicketType.FAN_MEMBER;
  const disableNamingBtn = invalidHolder || nonTransferable || fanMember;
  return (
    <Styles.WrapperNaming id={`naming-ticket-wrapper-${id}`}>

      {isPassport || <AsideTimelineDate eventDate={namingData.dates[0]} isLastItem={isLastItem} id={id} />}

      <Styles.WrapperTicketInfo
        $error={userNotFound && !holder}
        id={`ticket-info-wrapper-${id}`}
      >

        <Styles.WrapperTicketTitle id={`ticket-title-wrapper-${id}`}>
          <Styles.ContainerTitle id={`ticket-title-container-${id}`}>
            <Styles.Title id={`ticket-title-${id}`}>
              {namingData.groupName}
            </Styles.Title>
            <Styles.Subtitle id={`ticket-subtitle-${id}`}>
              {`${namingData.name} | ${formatNumericValue({ value : namingData.price, currencyFormat })}`}
            </Styles.Subtitle>
            {isPassport && (
              <DateTimeSessionsTags dates={namingData.dates} />
            )}
          </Styles.ContainerTitle>
          {!isFutebol && <TagNaming
            transferMandatoryFaceRecognition={transferMandatoryFaceRecognition}
            faceRecognition={namingData.faceRecognition}
          />}
        </Styles.WrapperTicketTitle>
        <Fragment>
          <Styles.WrapperInput id={`input-wrapper-${id}`}
            onSubmit={formik.handleSubmit}
            onBlur={() => {
              formik.handleBlur;
            }}
          >
            <Styles.ContainerInput id={`input-container-${id}`}>
              {holder ? (
                <UserNaming
                  isAKidsTicket={isAKidsTicket}
                  theresAHolderAndItsMe={theresAHolderAndItsMe} holder={holder}
                />
              ) : (
                <Fragment>
                  {namingData.isTransferableGuestType && (
                    <Fragment>
                      {isAKidsTicket ? (
                        <KidsNaming ticketId={props.namingData.idSpecificInIdentifyList} />
                      ) : (
                        <Fragment>
                          <Styles.InputForm
                            id={`input-email-${id}`}
                            name="email"
                            disabled={false}
                            $containsError={Boolean(containsError)}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            type="text"
                            placeholder={translate(['INFORMATION', 'RECIPIENT_EMAIL'])}
                          />
                          <Styles.EmailIcon src={ImagesApp.svg.icEmail} id={`email-icon-${id}`} />
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </Styles.ContainerInput>
            {containsError && isMobile && (
              <Styles.ErrorText id='error-text-naming-email'>
                {dontHasUserWarning ? (
                  translate(['INFORMATION', 'ASSIGN_USER_HASNT_ACCOUNT_WARNING'])
                ) : formik.errors.email}
              </Styles.ErrorText>
            )}
            <Styles.NamingButton
              id={`naming-button-${id}`}
              disabled={disableNamingBtn}
              type={isAKidsTicket ? 'button' : 'submit'}
              onClick={handleClick}>
              {translate(['INFORMATION', holder ? 'EDIT_ASSIGN' : 'TO_ASSIGN'])}
            </Styles.NamingButton>

            {containsError && !isMobile && (
              <Styles.ErrorText id='error-text-naming-email'>
                {dontHasUserWarning ? (
                  translate(['INFORMATION', 'ASSIGN_USER_HASNT_ACCOUNT_WARNING'])
                ) : formik.errors.email}
              </Styles.ErrorText>
            )}
          </Styles.WrapperInput>
          {userHasntBepassToken && <ErrorAlert hideErrorMessage={hideErrorMessage} />}
          {(!holder && !isAKidsTicket) && (
            <HolderTicketCheckbox
              handleChecked={handleChecked}
              holder={holder}
              isAKidsTicket={isAKidsTicket}
              sessions={namingData.dates}
              isBepass={namingData.faceRecognition}
              id={id}
            />
          )}
        </Fragment>
        {(holder && !theresAHolderAndItsMe) && (
          <Styles.TransferDescriptionParagraph>
            <span>
              {translate([
                'INFORMATION',
                isAKidsTicket ? (
                  'DEPENDENT_TRANSFER'
                ) : 'COMPANION_TRANSFER',
              ])}
            </span>
            <strong>
              {translate([
                'INFORMATION',
                isAKidsTicket ? (
                  'DEPENDENT_TRANSFER_BOLD'
                ) : 'COMPANION_TRANSFER_BOLD',
              ])}
            </strong>
          </Styles.TransferDescriptionParagraph>
        )}
      </Styles.WrapperTicketInfo>
    </Styles.WrapperNaming>
  );
};

export default NamingTicket;
